import * as React from 'react';
import { useEffect, useState } from 'react';

import { NavLink, useLocation } from 'react-router-dom';

import PropTypes from 'prop-types';

import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import SidenavCollapse from 'components/Sidenav/SidenavCollapse'; // Custom styles for the Sidenav
import SidenavRoot from 'components/Sidenav/SidenavRoot';
import sidenavLogoLabel from 'components/Sidenav/styles/sidenav'; // Material Dashboard 2 React context
import { setMiniSidenav, useMaterialUIController } from 'context';
import { useIntl } from 'react-intl';
import { getImageURL, getUrlParameter } from 'utils/urlUtils';
import app from 'config/app.json';
import useConf from 'hook/useConf';
import { useGetConfigurationQuery } from 'services/applicationApi';
import { NOT_FOUND } from 'utils/messageUtils';
import { themeOptions } from 'assets/theme';
import { themeOptions as darkThemeOptions } from 'assets/theme-dark';
import { Close } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import ArcadePortal from 'components/Logo/ArcadePortal';
import { URL_PORTAL_PATH } from 'utils/navigationUtils';

function Sidenav({ routes, brandName, ...rest }) {
  const { formatMessage: msg } = useIntl();
  const { controller, dispatch } = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;

  const [transparent, setTransparent] = useState(
    window.innerWidth < 1200 ? false : transparentSidenav
  );
  const [white, setWhite] = useState(window.innerWidth < 1200 ? false : whiteSidenav);

  const location = useLocation();

  const sidenavWidth = 250;
  const logoCode = `logo.${(transparent && !darkMode) || white ? 'light' : 'dark'}`;
  const { getValue: conf } = useConf(useGetConfigurationQuery);
  const customLogo = conf(`ui.image.${logoCode}`);
  const currentAppCode = getUrlParameter(window.location, app.url.paramApp);
  const [logoSource, setLogoSource] = useState(
    customLogo ? getImageURL(logoCode, currentAppCode) : null
  );
  useEffect(
    () => setLogoSource(customLogo ? getImageURL(logoCode, currentAppCode) : null),
    [currentAppCode, customLogo, logoCode]
  );

  let textColor = 'white';

  if (transparent || (white && !darkMode)) {
    textColor = 'dark';
  } else if (white && darkMode) {
    textColor = 'inherit';
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);

      setTransparent(window.innerWidth < 1200 ? false : transparentSidenav);
      setWhite(window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /**
     The event listener that's calling the handleMiniSidenav function when resizing the window.
     */
    window.addEventListener('resize', handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleMiniSidenav);
  }, [dispatch, location, transparentSidenav, whiteSidenav]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({
      type,
      visibleInMenu = true,
      name,
      icon,
      title,
      noCollapse,
      key,
      href,
      viewType,
      view,
      onClick = () => {}
    }) => {
      function isActive(viewPathname) {
        return (
          location.pathname.endsWith(viewPathname) || location.pathname.includes(`${viewPathname}/`)
        );
      }

      const label = msg({ id: `page.${key}`, defaultMessage: name });
      const path = `${viewType}${view ? `/${view}` : ''}`;

      let returnValue;
      if (type === 'collapse' && visibleInMenu) {
        returnValue = href ? (
          <li key={`li-${key}`}>
            <Link
              href={href}
              key={key}
              target="_blank"
              onClick={onClick}
              rel="noreferrer"
              sx={{ textDecoration: 'none' }}
            >
              <SidenavCollapse
                name={label}
                icon={icon}
                active={isActive(path)}
                noCollapse={noCollapse}
                ownerState={{
                  transparentSidenav: transparent,
                  whiteSidenav: white,
                  miniSidenav,
                  darkMode,
                  sidenavWidth
                }}
              />
            </Link>
          </li>
        ) : (
          <li key={`li-${key}`}>
            <NavLink key={key} to={`${URL_PORTAL_PATH}${path}`} onClick={onClick}>
              <SidenavCollapse
                name={label}
                icon={icon}
                active={isActive(path)}
                ownerState={{
                  transparentSidenav: transparent,
                  whiteSidenav: white,
                  miniSidenav,
                  darkMode,
                  sidenavWidth
                }}
              />
            </NavLink>
          </li>
        );
      } else if (type === 'title') {
        returnValue = (
          <MDTypography
            key={key}
            component="div"
            color={textColor}
            variant="caption"
            sx={{
              fontWeight: 'bold',
              textTransform: 'uppercase',
              pl: 3,
              mt: 2,
              mb: 1,
              ml: 1
            }}
          >
            {title}
          </MDTypography>
        );
      } else if (type === 'divider') {
        returnValue = (
          <Divider
            key={key}
            light={(!darkMode && !white && !transparent) || (darkMode && !transparent && white)}
          />
        );
      }

      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{
        transparentSidenav: transparent,
        whiteSidenav: white,
        miniSidenav,
        darkMode,
        sidenavWidth
      }}
    >
      <MDBox component="nav" aria-label={msg({ id: `common.sidenav.label` })}>
        <MDBox pt={2} pb={0} px={4} textAlign="center">
          <MDBox
            aria-label={msg({ id: 'action.menu.hide' })}
            display={{ xs: 'block', xl: 'none' }}
            position="absolute"
            top={0}
            right={0}
            p={0}
            onClick={closeSidenav}
          >
            <IconButton aria-label={msg({ id: 'action.close' })}>
              <Close
                fontSize="medium"
                sx={{
                  color: (theme) => theme.palette.grey[500]
                }}
              />
            </IconButton>
          </MDBox>
          <MDBox
            component={NavLink}
            to="/home"
            display="flex"
            alignItems="center"
            sx={{
              justifyContent: 'center',
              flexDirection: 'column'
            }}
            aria-label={msg({ id: 'page.home.label' })}
          >
            {logoSource ? (
              <MDBox
                component="img"
                src={logoSource}
                alt="Logo"
                sx={{
                  maxWidth: '186px',
                  maxHeight: '80px'
                }}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null;
                  setLogoSource(null);
                }}
              />
            ) : (
              <ArcadePortal
                primaryColor={
                  (transparent && !darkMode) || white
                    ? themeOptions.palette.text.main
                    : darkThemeOptions.palette.text.main
                }
                dark={!((transparent && !darkMode) || white)}
              />
            )}
            {brandName && brandName !== NOT_FOUND && (
              <MDBox sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}>
                <MDTypography
                  component="h6"
                  variant="button"
                  fontWeight="medium"
                  color={textColor}
                  mt={1}
                >
                  {brandName && brandName !== NOT_FOUND ? brandName : ''}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
        <Divider
          light={(!darkMode && !white && !transparent) || (darkMode && !transparent && white)}
        />
        <List>{renderRoutes}</List>
      </MDBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: 'info'
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  color: PropTypes.oneOf(['primary', 'secondary', 'info', 'success', 'warning', 'error', 'dark']),
  brandName: PropTypes.string
};

export default Sidenav;
